import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import {
  GET_MY_SUBSCRIPTION,
  CREATE_PERSONAL_CHECKOUT,
  CANCEL_MY_SUBSCRIPTION,
  VERIFY_PERSONAL_CHECKOUT,
  GET_TENANT_SUBSCRIPTION,
} from '../graphql/billing';
import { User, UserSubscription, TenantSubscription } from '../types';
import { computePermissions } from '../hooks/useUserRole';

interface BillingSectionProps {
  user: User;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '—';
  }
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    Active: { label: 'Active', color: 'success' },
    Pending: { label: 'Pending', color: 'warning' },
    Expired: { label: 'Expired', color: 'error' },
    Cancelled: { label: 'Cancelled', color: 'default' },
  };
  const cfg = map[status] ?? { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600 }} />;
}

export const BillingSection: React.FC<BillingSectionProps> = ({ user }) => {
  const roleId = user.roleId ?? 999;
  const { isClientAdmin: isAdmin, isPaidUser: isPaid } = computePermissions(user);

  // Derive primary tenant from user mappings (read-only status display only)
  const primaryTenantId = user.mappings?.[0]?.tenantId ?? '';
  const hasTenantMapping = !!primaryTenantId;

  const successUrl = `${window.location.origin}/portal?billing=success`;
  const cancelUrl = `${window.location.origin}/portal`;

  // Tenant subscription query (read-only)
  const { data: tenantSubData, loading: tenantSubLoading } = useQuery<{ tenantSubscription: TenantSubscription | null }>(GET_TENANT_SUBSCRIPTION, {
    variables: { tenantId: primaryTenantId },
    skip: !hasTenantMapping,
  });
  const tenantSub = tenantSubData?.tenantSubscription;

  // Verify on return from Square
  const [verifyCheckout] = useMutation<{ verifyPersonalCheckout: UserSubscription | null }>(VERIFY_PERSONAL_CHECKOUT);
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Personal subscription query
  const { data: mySubData, loading: mySubLoading, refetch: refetchMySub } = useQuery<{ mySubscription: UserSubscription | null }>(
    GET_MY_SUBSCRIPTION,
    { skip: !isPaid && !isAdmin },
  );
  const mySub = mySubData?.mySubscription;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingParam = params.get('billing');

    if (billingParam === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      const transactionId = params.get('transactionId');
      if (transactionId) {
        setVerifying(true);
        verifyCheckout({ variables: { transactionId } })
          .then(() => { setBillingSuccess(true); refetchMySub(); })
          .catch(() => setBillingSuccess(true))
          .finally(() => setVerifying(false));
      } else {
        setBillingSuccess(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [createPersonalCheckout, { loading: checkoutLoading, error: checkoutError }] = useMutation<{ createPersonalCheckout: { checkoutUrl: string } }>(CREATE_PERSONAL_CHECKOUT);
  const [cancelMySub, { loading: cancelMySubLoading }] = useMutation(CANCEL_MY_SUBSCRIPTION);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const handlePersonalCheckout = async (planId: string) => {
    setActionError(null);
    try {
      const { data } = await createPersonalCheckout({ variables: { planId, successUrl, cancelUrl } });
      if (data?.createPersonalCheckout?.checkoutUrl) {
        window.location.href = data.createPersonalCheckout.checkoutUrl;
      }
    } catch (err: any) {
      setActionError(err.message || 'Failed to start checkout. Please try again.');
    }
  };

  const handleCancelPersonal = async () => {
    setActionError(null);
    try {
      await cancelMySub();
      await refetchMySub();
    } catch (err: any) {
      setActionError(err.message || 'Failed to cancel subscription.');
    }
  };

  return (
    <>
      {/* Personal Subscription Card */}
      <Card elevation={2} sx={{ borderRadius: 3, mb: hasTenantMapping ? 3 : 0 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <CreditCardIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" fontWeight={600}>
              Subscription
            </Typography>
          </Box>
          <Divider sx={{ mb: 2.5 }} />

          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}

          {checkoutError && (
            <Alert severity="error" sx={{ mb: 2 }}>{checkoutError.message}</Alert>
          )}

          {/* Free user — choose a plan */}
          {!isPaid && !isAdmin && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You are currently on the <strong>Free</strong> plan. Subscribe to unlock cloud sync, custom symbols, and more.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handlePersonalCheckout('personal-monthly')}
                  disabled={checkoutLoading}
                  startIcon={checkoutLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Monthly — $14.99 / mo
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handlePersonalCheckout('personal-annual')}
                  disabled={checkoutLoading}
                  startIcon={checkoutLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Annual — $12.99 / mo
                </Button>
              </Box>
            </Box>
          )}

          {/* Paid/admin user — show subscription details */}
          {(isPaid || isAdmin) && (
            <>
              {mySubLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : mySub ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Plan</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <StarIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                      <Typography variant="body2" fontWeight={600}>
                        {mySub.planId === 'personal-monthly' ? 'Personal (Monthly)'
                          : mySub.planId === 'personal-annual' ? 'Personal (Annual)'
                          : mySub.planId === 'personal' ? 'Personal'
                          : mySub.planId}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <StatusChip status={mySub.status} />
                    </Box>
                  </Grid>

                  {mySub.startDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Started</Typography>
                      <Typography variant="body2" fontWeight={500}>{formatDate(mySub.startDate)}</Typography>
                    </Grid>
                  )}

                  {mySub.expiryDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Renews / Expires</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: mySub.status === 'Active' ? 'success.main' : 'error.main' }} />
                        <Typography variant="body2" fontWeight={500}>{formatDate(mySub.expiryDate)}</Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {(mySub.status === 'Expired' || mySub.status === 'Cancelled') && (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handlePersonalCheckout('personal-monthly')}
                            disabled={checkoutLoading}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            Monthly — $14.99 / mo
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePersonalCheckout('personal-annual')}
                            disabled={checkoutLoading}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            Annual — $12.99 / mo
                          </Button>
                        </>
                      )}
                      {mySub.status === 'Active' && (
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={() => setConfirmCancelOpen(true)}
                          disabled={cancelMySubLoading}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          {cancelMySubLoading ? 'Cancelling…' : 'Cancel subscription'}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: isAdmin ? 0 : 2 }}>
                    {isAdmin
                      ? 'You have admin access. Personal subscription not required.'
                      : isPaid
                        ? 'Your organisation subscription is active. You can also subscribe personally.'
                        : 'No active personal subscription found.'}
                  </Typography>
                  {!isAdmin && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handlePersonalCheckout('personal-monthly')}
                        disabled={checkoutLoading}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        Monthly — $14.99 / mo
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handlePersonalCheckout('personal-annual')}
                        disabled={checkoutLoading}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        Annual — $12.99 / mo
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Cancel subscription confirmation dialog */}
      <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)}>
        <DialogTitle>Cancel subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your subscription will be cancelled immediately and you will lose access to paid features. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelOpen(false)} sx={{ textTransform: 'none' }}>
            Keep subscription
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelMySubLoading}
            onClick={async () => {
              setConfirmCancelOpen(false);
              await handleCancelPersonal();
            }}
            sx={{ textTransform: 'none' }}
          >
            {cancelMySubLoading ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verifying payment */}
      <Snackbar open={verifying} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ width: '100%' }}>
          Verifying payment…
        </Alert>
      </Snackbar>

      {/* Billing success */}
      <Snackbar
        open={billingSuccess}
        autoHideDuration={6000}
        onClose={() => setBillingSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setBillingSuccess(false)} sx={{ width: '100%' }}>
          Payment successful! Your subscription is now active.
        </Alert>
      </Snackbar>

      {/* Organisation Plan — read-only status */}
      {hasTenantMapping && (
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <BusinessIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Organisation Plan
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            {tenantSubLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : tenantSub && tenantSub.status === 'Active' ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Plan</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <GroupsIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                    <Typography variant="body2" fontWeight={600}>Institutional</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip status={tenantSub.status} />
                  </Box>
                </Grid>

                {tenantSub.startDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Started</Typography>
                    <Typography variant="body2" fontWeight={500}>{formatDate(tenantSub.startDate)}</Typography>
                  </Grid>
                )}

                {tenantSub.expiryDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Expires</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight={500}>{formatDate(tenantSub.expiryDate)}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {tenantSub
                  ? `Organisation subscription is ${tenantSub.status.toLowerCase()}.`
                  : 'No active organisation subscription. Contact your administrator to activate one.'}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};
